 
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProjectsFinance } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { PROJECT_STATUS, formatEur } from '../../lib/workStatus';
import { Coins, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const ProjectProfit = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProjectsFinance().then(setData).catch(() => toast.error('Neizdevās ielādēt datus')).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="admin-profit-loading"><div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const projects = data?.projects || [];
  const totals = data?.totals || { client_amount: 0, total_costs: 0, profit: 0 };

  return (
    <div className="space-y-6" data-testid="admin-profit-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1F3D2B] flex items-center gap-2" data-testid="admin-profit-title"><Coins className="w-6 h-6" /> Objektu peļņa</h1>
        <p className="text-[#2E2E2E]/70">Klientam izrakstāmā summa − darbinieku, transporta, degvielas, krāsas un citas izmaksas. Redzams tikai administratoram.</p>
      </div>

      <div className="grid grid-cols-3 gap-3" data-testid="admin-profit-totals">
        <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-[#2E2E2E]/60">Izrakstāms klientiem</p><p className="text-2xl font-bold text-[#1F3D2B]" data-testid="admin-profit-total-client">{formatEur(totals.client_amount)}</p></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-[#2E2E2E]/60">Izmaksas kopā</p><p className="text-2xl font-bold text-red-700" data-testid="admin-profit-total-costs">{formatEur(totals.total_costs)}</p></CardContent></Card>
        <Card className="border-0 shadow-sm bg-[#1F3D2B] text-white"><CardContent className="p-4"><p className="text-xs text-white/70">Peļņa kopā</p><p className="text-2xl font-bold" data-testid="admin-profit-total-profit">{formatEur(totals.profit)}</p></CardContent></Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f8faf8] text-left">
              <tr><th className="px-4 py-3">Objekts</th><th className="px-4 py-3">Klients</th><th className="px-4 py-3">Statuss</th><th className="px-4 py-3 text-right">Izrakstāms</th><th className="px-4 py-3 text-right">Darbinieki</th><th className="px-4 py-3 text-right">Transports</th><th className="px-4 py-3 text-right">Degviela</th><th className="px-4 py-3 text-right">Krāsa</th><th className="px-4 py-3 text-right">Citas</th><th className="px-4 py-3 text-right">Peļņa</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {projects.map((p) => {
                const st = PROJECT_STATUS[p.status] || { label: p.status, className: '' };
                return (
                  <tr key={p.project_id} className="hover:bg-[#f8faf8]" data-testid={`admin-profit-row-${p.project_id}`}>
                    <td className="px-4 py-3 font-medium text-[#1F3D2B]">{p.project_name}</td>
                    <td className="px-4 py-3 text-[#2E2E2E]/70">{p.client_name || '-'}</td>
                    <td className="px-4 py-3"><Badge className={st.className}>{st.label}</Badge></td>
                    <td className="px-4 py-3 text-right">{formatEur(p.client_amount)}</td>
                    <td className="px-4 py-3 text-right text-red-700">−{formatEur(p.employee_cost)}</td>
                    <td className="px-4 py-3 text-right text-red-700">−{formatEur(p.transport_cost)}</td>
                    <td className="px-4 py-3 text-right text-red-700">−{formatEur(p.fuel_cost)}</td>
                    <td className="px-4 py-3 text-right text-red-700">−{formatEur(p.paint_cost)}</td>
                    <td className="px-4 py-3 text-right text-red-700">−{formatEur(p.other_cost)}</td>
                    <td className={`px-4 py-3 text-right font-bold ${p.profit >= 0 ? 'text-green-700' : 'text-red-700'}`} data-testid={`admin-profit-value-${p.project_id}`}>{formatEur(p.profit)} <span className="text-xs font-normal">({p.margin_percent}%)</span></td>
                    <td className="px-4 py-3"><Link to={`/portal/admin/projects/${p.project_id}`} className="text-[#1F3D2B]" data-testid={`admin-profit-open-${p.project_id}`}><ChevronRight className="w-4 h-4" /></Link></td>
                  </tr>
                );
              })}
              {projects.length === 0 && <tr><td colSpan={11} className="px-4 py-8 text-center text-[#2E2E2E]/60" data-testid="admin-profit-empty">Nav objektu.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectProfit;
